import { useGlobalEvent } from 'beautiful-react-hooks'
import cx from 'classnames'
import { distributeColumns } from 'common/Prose/Commands/table/mutation'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { TABLE_PROMPT_MAX_SIZE } from 'common/Prose/constants'
import { getNextTextPosition } from 'common/Prose/utils/getNextTextPosition'
import { findTableFromPos } from 'common/Prose/utils/table'
import { useArticleMeta } from 'hooks/useArticleMeta'
import { useCurrentArticleId } from 'hooks/useCurrentArticleId'
import { useStateRef } from 'hooks/useStateRef'
import { useFeaturesWithoutUserOverrides } from 'hooks/queries/flipper/useFeatures'
import { schema } from 'prose-core'
import { SlashMenuItem, slashMenuItems } from 'proseShared/SlashMenuItems'
import { Fragment, Node as ProseNode } from 'prosemirror-model'
import { TextSelection } from 'prosemirror-state'
import * as React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArticleMeta } from 'types'
import { useDetectOutsideClick } from 'utils/hookUtils'
import { FloatingMenuAnchorPluginKey } from './FloatingMenuAnchorPlugin'
import styles from './FloatingMenuAnchorPlugin.module.scss'
import { FloatingMenuItemsContainer } from './FloatingMenuItemsContainer/FloatingMenuItemsContainer'
import { useModals } from 'almanac-ui/Modal'

export type FloatingMenuProps = {}

export const FloatingMenuComponent: React.FC<React.PropsWithChildren<FloatingMenuProps>> = (props) => {
  const [filter, setFilter, _filter] = useStateRef('')
  const [direction, setDirection] = useState('up')
  const [selectionIndex, setSelectionIndex, _selectionIndex] = useStateRef(0)
  const currentArticleId = useCurrentArticleId()
  const article = useArticleMeta(currentArticleId) as ArticleMeta
  const selfRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const view = useEditorView()
  const { data: featureFlags = [] } = useFeaturesWithoutUserOverrides()
  const stackManager = useModals()

  const handleScroll = () => {
    maybeFlipDirection()
  }

  const maybeFlipDirection = () => {
    const positionRect = selfRef.current?.getBoundingClientRect()
    const sizeRect = listRef.current?.getBoundingClientRect()
    if (positionRect && sizeRect) {
      if (positionRect.y + sizeRect.height < window.innerHeight) {
        setDirection('down')
      } else if (direction === 'down') {
        setDirection('up')
      }
    }
  }

  useLayoutEffect(() => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    document.getElementById('main-content')!.addEventListener('scroll', handleScroll)
    maybeFlipDirection()
    return () => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      document.getElementById('main-content')!.removeEventListener('scroll', handleScroll)
    }
  })

  const anchorEl = document.getElementById('floating-menu-anchor')

  useEffect(() => {
    const matchedText = anchorEl?.dataset.matchedText
    if (matchedText && filter !== matchedText) {
      setSelectionIndex(0)
      setFilter(matchedText)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl])

  const filteredItems = (filterBy: string) => {
    const items = slashMenuItems({ article, tableMaxSize: TABLE_PROMPT_MAX_SIZE, featureFlags, stackManager })
    const matchRegexp = new RegExp(filterBy.split('').join('.*'), 'i')
    return items.map((group) => {
      let filteredItems = group.elements
        .filter((elem) => elem.enable(view.state))
        .filter((elem) => matchRegexp.test(elem.label))

      group.elements = filteredItems
      return group
    })
  }

  const onKeyDownHandler = (e: KeyboardEvent) => {
    if (!document.getElementById('floating-menu-anchor')) return
    const numberFilteredItems = filteredItems(_filter.current).flatMap((group) => group.elements).length
    if (!numberFilteredItems) return
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault() // prevent a linebreak from being added
      e.stopPropagation() // prevent the enter cascading to any menus that might open
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const selfRect = selfRef.current!.getBoundingClientRect()
      const chosenElement = filteredItems(_filter.current).flatMap((group) => group.elements)[_selectionIndex.current]
      const queryLength = _filter.current.length
      const position = { top: selfRect.top, left: selfRect.left }

      const triggerPos = view.state.selection.from - queryLength

      createElement(chosenElement, triggerPos, position, queryLength)
    }
    if (['Backspace', 'Escape'].includes(e.key)) {
      closeMenu()
    }
    if (e.key === '/') {
      setFilter('')
    }
    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      setSelectionIndex((oldIdx) => {
        const newIdx = (oldIdx + (e.key === 'ArrowUp' ? -1 : 1)) % numberFilteredItems
        return newIdx < 0 ? numberFilteredItems - 1 : newIdx
      })
      e.preventDefault()
    }
  }

  const onKeyDown = useGlobalEvent('keydown', { capture: true })
  onKeyDown((e) => {
    // @ts-ignore
    onKeyDownHandler(e)
  })

  const closeMenu = () => {
    const tr = view.state.tr
    setFilter('')
    setSelectionIndex(0)
    view.dispatch(tr.setMeta(FloatingMenuAnchorPluginKey, 'clear'))
  }

  useDetectOutsideClick(selfRef, closeMenu)

  useEffect(() => {
    const numberFilteredItems = filteredItems(filter).flatMap((group) => group.elements).length
    if (numberFilteredItems === 0) closeMenu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const createElement = async (
    chosenElement: SlashMenuItem,
    triggerPos: number,
    position: { top: number; left: number },
    queryLength: number,
  ) => {
    const {
      state: {
        tr,
        tr: {
          selection: { from },
        },
      },
    } = view

    const anchorRes = tr.doc.resolve(from - (queryLength + 1))
    const headRes = tr.doc.resolve(from)
    const selection = new TextSelection(anchorRes, headRes)
    tr.setSelection(selection)
    tr.deleteSelection()
    const getNode = chosenElement.create(view, position, triggerPos, queryLength)
    closeMenu()

    const node = await getNode
    if (!node) return

    const {
      selection: {
        $anchor,
        $anchor: { parent },
      },
    } = tr
    let insertPosition: number
    if (parent && parent.type === schema.nodes.paragraph && parent.content.size === 0) {
      // Empty paragraph, replace with new element
      insertPosition = $anchor.pos - $anchor.parentOffset

      tr.replaceWith(insertPosition - 1, insertPosition - 1 + parent.nodeSize, node)
      tr.setSelection(TextSelection.create(tr.doc, getNextTextPosition(tr.doc.resolve(insertPosition - 1)).pos))
      chosenElement.postInsert?.(insertPosition - 1, tr)
    } else {
      // Otherwise append new element and select
      insertPosition = $anchor.pos - $anchor.parentOffset + parent.nodeSize - 1

      // inline nodes (or fragments which start with an inline node) get inserted into the current node
      if (node instanceof Fragment ? node.firstChild?.isInline : node.isInline) insertPosition = $anchor.pos

      tr.insert(insertPosition, node)
      tr.setSelection(TextSelection.create(tr.doc, getNextTextPosition(tr.doc.resolve(insertPosition)).pos))
      chosenElement.postInsert?.(insertPosition, tr)
    }
    if ((node as ProseNode).type === schema.nodes.table) {
      setTimeout(() => {
        const table = findTableFromPos(insertPosition + 2, view.state.doc)
        if (table) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const tableNodeViewEl = view.nodeDOM(table.pos)! as HTMLElement
          distributeColumns(tableNodeViewEl.offsetWidth, table.pos)(view.state, view.dispatch)
        }
      }, 0)
    }

    view.dispatch(tr)
    view.focus()
  }

  const handleItemClick = (idx: number) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const selfRect = selfRef.current!.getBoundingClientRect()
    const chosenElement = filteredItems(filter).flatMap((group) => group.elements)[idx]
    const position = { top: selfRect.top, left: selfRect.left }
    const queryLength = _filter.current.length

    const triggerPos = view.state.selection.from - queryLength
    createElement(chosenElement, triggerPos, position, queryLength)
  }

  return anchorEl
    ? createPortal(
        <div
          ref={selfRef}
          className={cx(styles.FloatingMenu, styles[direction])}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div ref={listRef} className={styles.menuWrapper}>
            <FloatingMenuItemsContainer
              items={filteredItems(filter)}
              selected={selectionIndex}
              setSelected={setSelectionIndex}
              onItemClick={handleItemClick}
            />
          </div>
        </div>,
        anchorEl,
      )
    : null
}
